/**
 * Iter-109 — Theme Context (Light / Dark mode)
 *
 * OnSetFlow shipped V1 with a permanent dark UI optimised for the SCG
 * tent + studio environment. Production users on outdoor shoots
 * (trailers, exteriors, daytime locations) reported the dark canvas
 * felt washed-out under bright sunlight, so this context introduces a
 * persistent user-level theme preference.
 *
 * Storage:
 *   localStorage["onsetflow:theme"] ∈ { "dark", "light" }
 * Default:
 *   dark (preserves the existing experience for everyone who hasn't
 *   opted in).
 *
 * Apply mechanism:
 *   We add/remove the `dark` class on `<html>` so Tailwind's
 *   `darkMode: ["class"]` config (see tailwind.config.js) drives every
 *   shadcn/ui token from the same source of truth. A matching CSS
 *   override layer in index.css remaps the hand-rolled
 *   text-white/text-neutral-XXX/bg-white-alpha utility classes that
 *   were baked into V1 components so we don't have to refactor 1000+
 *   className occurrences for the light-mode launch.
 */

import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";

const STORAGE_KEY = "onsetflow:theme";
const VALID = new Set(["dark", "light"]);

const ThemeContext = createContext({
    theme: "light",
    setTheme: () => {},
    toggleTheme: () => {},
});

function readInitial() {
    if (typeof window === "undefined") return "light";
    try {
        const stored = window.localStorage.getItem(STORAGE_KEY);
        // Iter-110 — Light is now the default for first-time users
        // (production users on real shoots reported the dark canvas
        // washes out under daylight). Anyone who previously toggled
        // their preference still wins: their localStorage value is
        // honoured. Only when the slot is empty do we fall through to
        // the new Light default.
        if (stored && VALID.has(stored)) return stored;
    } catch (_) {
        // localStorage may throw in SSR / private mode — fall through to
        // the default and silently skip persistence.
    }
    return "light";
}

export function ThemeProvider({ children }) {
    const [theme, setThemeState] = useState(readInitial);

    // Apply the class on <html> on every change. We deliberately mutate
    // the documentElement directly rather than via state so the FIRST
    // render already reflects the user's stored preference (avoids a
    // "flash of dark mode" for a returning light-mode user). React
    // strict-mode double-mount is fine — adding/removing an idempotent
    // class is a no-op on the second pass.
    useEffect(() => {
        const root = document.documentElement;
        if (theme === "dark") {
            root.classList.add("dark");
            root.classList.remove("light");
        } else {
            root.classList.add("light");
            root.classList.remove("dark");
        }
        // Mirror to <meta name="theme-color"> so the mobile browser
        // chrome (status bar tint on iOS / address bar on Android)
        // matches the in-app canvas — small but very obvious polish.
        const meta = document.querySelector('meta[name="theme-color"]');
        if (meta) {
            meta.setAttribute(
                "content",
                theme === "dark" ? "#28282b" : "#f5f5f5"
            );
        }
        try {
            window.localStorage.setItem(STORAGE_KEY, theme);
        } catch (_) {
            // private mode / quota — ignore, the in-memory state still works
            // for the lifetime of the tab.
        }
    }, [theme]);

    const setTheme = useCallback((next) => {
        if (!VALID.has(next)) return;
        setThemeState(next);
    }, []);

    const toggleTheme = useCallback(() => {
        setThemeState((cur) => (cur === "dark" ? "light" : "dark"));
    }, []);

    const value = useMemo(
        () => ({ theme, setTheme, toggleTheme }),
        [theme, setTheme, toggleTheme]
    );

    return (
        <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
    );
}

export function useTheme() {
    return useContext(ThemeContext);
}
